// Types defined locally to avoid circular import with price-tables.ts
type BrazilianUF =
  | 'AC'
  | 'AL'
  | 'AP'
  | 'AM'
  | 'BA'
  | 'CE'
  | 'DF'
  | 'ES'
  | 'GO'
  | 'MA'
  | 'MT'
  | 'MS'
  | 'MG'
  | 'PA'
  | 'PB'
  | 'PR'
  | 'PE'
  | 'PI'
  | 'RJ'
  | 'RN'
  | 'RS'
  | 'RO'
  | 'RR'
  | 'SC'
  | 'SP'
  | 'SE'
  | 'TO';

interface IPriceTableConditionGroup {
  id: string;
  priority?: boolean;
  conditions: {
    id: string;
    salesFor?: BrazilianUF[];
    billingLimitInCents?: string;
    toBillFor?: string;
  }[];
}

export interface ValidationResult {
  status: 'green' | 'yellow' | 'red';
  messages: string[];
}

const ALL_UFS: BrazilianUF[] = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

interface Condition {
  id: string;
  salesFor?: BrazilianUF[];
  billingLimitInCents?: string;
  toBillFor?: string;
}

interface GroupWithPriority extends IPriceTableConditionGroup {
  priorityEnabled?: boolean;
}

export function validatePriceTableConditions(groups: GroupWithPriority[]): ValidationResult {
  const totalConds = groups.reduce((acc, g) => acc + (g.conditions?.length ?? 0), 0);

  if (totalConds === 0) {
    return {
      status: 'red',
      messages: [
        'Nenhuma condição foi configurada, favor configurar ao menos uma condição antes de realizar a validação!',
      ],
    };
  }

  const problems: string[] = [];

  // 1) UF coverage
  const cov = validateCoverage(groups);
  if (!cov.ok) {
    const msg = cov.missing.length
      ? `É obrigatório que todos os estados estejam incluídos em condições. Faltando: ${cov.missing.join(', ')}.`
      : 'É obrigatório que todos os estados estejam incluídos em condições.';
    problems.push(msg);
  }

  // 2) Specific states with limit require fallback without limit
  const fb = validateStateFallbacks(groups);
  if (!fb.ok) {
    problems.push(
      `Falta condição de fallback (sem limite) para os seguintes estados com limite específico: ${fb.missing.join(', ')}.`,
    );
  }

  // 3) Company required
  const comp = validateCompanyRequired(groups);
  if (!comp.ok) {
    problems.push('Em cada condição é obrigatório faturar para uma empresa.');
  }

  // 4) Duplicates
  const dups = findDuplicates(groups);
  if (dups.length > 0) {
    problems.push('Não é possível cadastrar condições duplicadas.');
  }

  // 5) ALL group cannot have only limited conditions
  const onlyLim = validateGroupOnlyLimitedWhenAll(groups);
  if (!onlyLim.ok) {
    problems.push(
      'Não é permitido o cadastro apenas de condições com limite de faturamento no mesmo grupo, cadastre ao menos uma condição sem limite.',
    );
  }

  // 6) Priority per group
  const prio = validatePriorityGroups(groups);
  if (!prio.ok) {
    problems.push(
      'Para habilitar a prioridade de faturamento de um grupo é necessário conter limite de faturamento em todas as condições do grupo, com exceção da última que deverá estar sem limite cadastrado.',
    );
  }

  if (problems.length > 0) {
    return { status: 'yellow', messages: problems };
  }

  return { status: 'green', messages: ['Condições validadas com sucesso!'] };
}

/* ===================== Helpers ===================== */

function parseNumberPTBR(value?: string): number | null {
  if (!value) return null;
  const s = value.trim().replace(/\./g, '').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function isUnlimited(limit?: string): boolean {
  const n = parseNumberPTBR(limit);
  return !n || n <= 0;
}

function isALL(salesFor?: BrazilianUF[]): boolean {
  return !salesFor || salesFor.length === 0;
}

function limitKey(limit?: string) {
  const n = parseNumberPTBR(limit);
  if (!n || n <= 0) return 'UNL';
  return String(n);
}

function statesKey(salesFor?: BrazilianUF[]): string {
  if (isALL(salesFor)) return 'ALL';
  const arr = Array.from(new Set(salesFor ?? []));
  arr.sort();
  return arr.join('|');
}

function billKey(toBillFor?: string): string {
  return (toBillFor ?? '').trim().toUpperCase();
}

function validateCoverage(groups: GroupWithPriority[]) {
  const conditions = groups.flatMap((g) => g.conditions ?? []);

  if (conditions.some((c) => isALL(c.salesFor))) {
    return { ok: true, missing: [] as BrazilianUF[] };
  }

  const covered = new Set<BrazilianUF>();
  for (const c of conditions) {
    (c.salesFor ?? []).forEach((uf) => {
      covered.add(uf);
    });
  }
  const missing = ALL_UFS.filter((uf) => !covered.has(uf));
  return { ok: missing.length === 0, missing };
}

function validateStateFallbacks(groups: GroupWithPriority[]) {
  const isActive = (c: Condition) => !!c?.toBillFor?.trim();
  const active = groups.flatMap((g) => (g.conditions ?? []).filter(isActive));

  const statesWithLimitedSpecific = new Set<BrazilianUF>();
  active.forEach((c) => {
    if (!isALL(c.salesFor) && !isUnlimited(c.billingLimitInCents)) {
      (c.salesFor ?? []).forEach((uf) => {
        statesWithLimitedSpecific.add(uf);
      });
    }
  });

  if (statesWithLimitedSpecific.size === 0) {
    return { ok: true, missing: [] as BrazilianUF[] };
  }

  const statesWithUnlimitedSpecific = new Set<BrazilianUF>();
  active.forEach((c) => {
    if (!isALL(c.salesFor) && isUnlimited(c.billingLimitInCents)) {
      (c.salesFor ?? []).forEach((uf) => {
        statesWithUnlimitedSpecific.add(uf);
      });
    }
  });

  const missing = ALL_UFS.filter(
    (uf) => statesWithLimitedSpecific.has(uf) && !statesWithUnlimitedSpecific.has(uf),
  );

  return { ok: missing.length === 0, missing };
}

function validateCompanyRequired(groups: GroupWithPriority[]) {
  const missingSomewhere = groups.some((g) =>
    (g.conditions ?? []).some((c) => !c?.toBillFor?.trim()),
  );
  return { ok: !missingSomewhere };
}

function findDuplicates(groups: GroupWithPriority[]) {
  const isActive = (c: Condition) => !!c?.toBillFor?.trim();
  const map = new Map<string, number>();

  groups.forEach((g) => {
    (g.conditions ?? []).forEach((c) => {
      if (!isActive(c)) return;
      const key = `${statesKey(c.salesFor)}::${billKey(c.toBillFor)}::${limitKey(c.billingLimitInCents)}`;
      map.set(key, (map.get(key) ?? 0) + 1);
    });
  });

  const duplicates: string[] = [];
  map.forEach((count, key) => {
    if (count > 1) duplicates.push(key);
  });
  return duplicates;
}

function validateGroupOnlyLimitedWhenAll(groups: GroupWithPriority[]) {
  const isActive = (c: Condition) => !!c?.toBillFor?.trim();
  const invalidGroupIdx: number[] = [];

  groups.forEach((g, gi) => {
    const active = (g.conditions ?? []).filter(isActive);
    if (active.length === 0) return;

    const allAreALL = active.every((c) => isALL(c.salesFor));
    if (!allAreALL) return;

    const hasUnlimited = active.some((c) => isUnlimited(c.billingLimitInCents));
    if (!hasUnlimited) invalidGroupIdx.push(gi);
  });

  return { ok: invalidGroupIdx.length === 0, invalidGroupIdx };
}

function validatePriorityGroups(groups: GroupWithPriority[]) {
  const invalid: number[] = [];

  groups.forEach((g, gi) => {
    const priorityEnabled = g.priority === true || g.priorityEnabled === true;
    if (!priorityEnabled) return;

    const list = g.conditions ?? [];
    if (list.length < 2) {
      invalid.push(gi);
      return;
    }

    const allButLastHaveLimit = list.slice(0, -1).every((c) => !isUnlimited(c.billingLimitInCents));
    const lastCondition = list[list.length - 1];
    const lastIsUnlimited = lastCondition ? isUnlimited(lastCondition.billingLimitInCents) : false;

    if (!(allButLastHaveLimit && lastIsUnlimited)) invalid.push(gi);
  });

  return { ok: invalid.length === 0, invalid };
}
